'use client'

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	ReactNode,
} from 'react'
import { SessionData } from '@/app/adapters/types'
import {
	UserCorePreferences,
	UserCorePrefsQuery,
	UserBusIntelPrefsQuery,
	UserBusIntelPreferences,
	UserRecruitPreferences,
	UserRecruitPrefsQuery,
	UserAgentProdPreferences,
} from '../../app/service/models/userPreferences'
import {
	getCoreUserPreferences,
	getBusIntelUserPreferences,
	putUserBusIntelPrefs,
	putUserAgentProdPrefs,
	putUserCorePrefs,
	getRecruitUserPreferences,
	putUserRecruitPrefs,
} from '../../app/service/userPreferencesService'
import { useCompanyContext } from './companyContext'

import { useUserContext } from './userContext'
import { PERMISSIONS } from '@/lib/constants/permissions'

interface PreferencesContextType {
	isLoading: boolean
	corePreferences: UserCorePreferences | null
	busIntelPreferences: UserBusIntelPreferences | null
	agentProdPreferences: UserAgentProdPreferences | null
	recruitPreferences: UserRecruitPreferences | null
	isBusIntelLoading: boolean
	refreshCorePreferences: (partyId: string) => Promise<void>
	refreshBusIntelPreferences: (partyId: string) => Promise<void>
	refreshAgentProdPreferences: (partyId: string) => Promise<void>
	refreshRecruitPreferences: (partyId: string) => Promise<void>
	updateCorePreferences: (prefs: UserCorePrefsQuery) => Promise<void>
	updateBusIntelPreferences: (prefs: UserBusIntelPreferences) => Promise<void>
	updateAgentProdPreferences: (prefs: UserAgentProdPreferences) => Promise<void>
	updateRecruitPreferences: (prefs: UserRecruitPreferences) => Promise<void>
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(
	undefined
)

export const PreferencesProvider: React.FC<{ sessionData: SessionData; children: ReactNode }> = ({
	sessionData,
	children,
}) => {
	const {
		selectedCompanyId,
		permissions,
		isLoading: isCompanyLoading,
	} = useCompanyContext()
	const { isLoading: isUserLoading } = useUserContext()

	const [corePreferences, setCorePreferences] =
		useState<UserCorePreferences | null>(null)
	const [busIntelPreferences, setBusIntelPreferences] =
		useState<UserBusIntelPreferences | null>(null)
	const [agentProdPreferences, setAgentProdPreferences] =
		useState<UserAgentProdPreferences | null>(null)
	const [recruitPreferences, setRecruitPreferences] =
		useState<UserRecruitPreferences | null>(null)

	const [isLoading, setIsLoading] = useState(false)
	const [isBusIntelLoading, setIsBusIntelLoading] = useState(false)

	const refreshCorePreferences = useCallback(
		async (partyId: string): Promise<void> => {
			if (!selectedCompanyId || !sessionData?.accessToken) return
			try {
				const prefs = await getCoreUserPreferences(
					{
						partyId,
						preferencesTypeCd: 'CORE',
					},
					sessionData?.accessToken
				)
				setCorePreferences(prefs ?? null)
			} catch (error) {
				console.error('Failed to fetch Core preferences:', error)
				setCorePreferences(null)
			}
		},
		[sessionData?.accessToken, selectedCompanyId]
	)

	const refreshBusIntelPreferences = useCallback(
		async (partyId: string): Promise<void> => {
			if (!selectedCompanyId || !sessionData?.accessToken) return

			setIsBusIntelLoading(true)
			try {
				const prefs = await getBusIntelUserPreferences(
					{
						partyId,
						preferencesTypeCd: 'BUSINTEL',
					},
					sessionData?.accessToken
				)
				setBusIntelPreferences(prefs ?? null)
			} catch (error) {
				console.error('Failed to fetch Bus Intel preferences:', error)
				setBusIntelPreferences(null)
			} finally {
				setIsBusIntelLoading(false)
			}
		},
		[selectedCompanyId, sessionData?.accessToken]
	)

	const refreshAgentProdPreferences = useCallback(
		async (partyId: string): Promise<void> => {
			if (!selectedCompanyId || !sessionData?.accessToken) return

			try {
				const prefs = await getBusIntelUserPreferences(
					{
						partyId,
						preferencesTypeCd: 'AGENTPROD',
					},
					sessionData?.accessToken
				)
				setAgentProdPreferences(prefs ?? null)
			} catch (error) {
				console.error('Failed to fetch Agent Prod preferences:', error)
				setAgentProdPreferences(null)
			}
		},
		[selectedCompanyId, sessionData?.accessToken]
	)

	const refreshRecruitPreferences = useCallback(
		async (partyId: string): Promise<void> => {
			if (!selectedCompanyId || !sessionData?.accessToken) return

			try {
				const prefs = await getRecruitUserPreferences(
					{
						partyId,
						preferencesTypeCd: 'RECRUIT',
					},
					sessionData?.accessToken
				)
				setRecruitPreferences(prefs ?? null)
			} catch (error) {
				console.error('Failed to fetch Recruit preferences:', error)
				setRecruitPreferences(null)
			}
		},
		[selectedCompanyId, sessionData?.accessToken]
	)

	const updateCorePreferences = useCallback(
		async (prefs: UserCorePrefsQuery) => {
			if (!selectedCompanyId) return

			try {
				const companyId = (prefs.partyId ?? selectedCompanyId)?.toUpperCase()
				if (!companyId) {
					throw new Error(
						'Company ID is required for updating core preferences'
					)
				}
				const newCorePrefs: UserCorePreferences = (await putUserCorePrefs(
					{ companyId, ...prefs },
					sessionData?.accessToken
				)) as UserCorePreferences
				setCorePreferences(newCorePrefs)
			} catch (error) {
				console.error('Failed to update core preferences:', error)
			}
		},
		[sessionData?.accessToken, selectedCompanyId]
	)

	const updateBusIntelPreferences = useCallback(
		async (prefs: UserBusIntelPreferences) => {
			const partyId = (prefs.companyId ?? selectedCompanyId)?.toUpperCase()
			if (!partyId) {
				throw new Error('Company ID is required for updating BI preferences')
			}
			const params: UserBusIntelPrefsQuery = {
				partyId,
				preferencesTypeCd: 'BUSINTEL',
			}
			const prefsToStore = {
				companyId: partyId,
				...prefs,
			}
			setBusIntelPreferences(prefsToStore)
			try {
				await putUserBusIntelPrefs(params, prefsToStore, sessionData?.accessToken)
			} catch (error) {
				console.error('Failed to update BI preferences:', error)
			}
		},
		[sessionData?.accessToken, selectedCompanyId]
	)

	const updateAgentProdPreferences = useCallback(
		async (prefs: UserAgentProdPreferences) => {
			const partyId = (prefs.companyId ?? selectedCompanyId)?.toUpperCase()
			if (!partyId) {
				throw new Error(
					'Company ID is required for updating Agent Prod preferences'
				)
			}
			const params: UserBusIntelPrefsQuery = {
				partyId,
				preferencesTypeCd: 'AGENTPROD',
			}
			const prefsToStore = {
				companyId: partyId,
				...prefs,
			}
			setAgentProdPreferences(prefsToStore)
			try {
				await putUserAgentProdPrefs(params, prefsToStore, sessionData?.accessToken)
			} catch (error) {
				console.error('Failed to update Agent Prod preferences:', error)
			}
		},
		[sessionData?.accessToken, selectedCompanyId]
	)

	const updateRecruitPreferences = useCallback(
		async (prefs: UserRecruitPreferences) => {
			const partyId = (prefs.companyId ?? selectedCompanyId)?.toUpperCase()
			if (!partyId) {
				throw new Error(
					'Company ID is required for updating recruit preferences'
				)
			}
			const params: UserRecruitPrefsQuery = {
				partyId,
				preferencesTypeCd: 'RECRUIT',
			}
			const prefsToStore = {
				companyId: partyId,
				...prefs,
			}
			try {
				await putUserRecruitPrefs(params, prefsToStore, sessionData?.accessToken)
				setRecruitPreferences(prefsToStore)
			} catch (error) {
				console.error('Failed to update Recruit preferences:', error)
			}
		},
		[sessionData?.accessToken, selectedCompanyId]
	)

	// ── Refs for stable async access in refresh() ────────────────────────
	const refreshCoreRef = useRef(refreshCorePreferences)
	refreshCoreRef.current = refreshCorePreferences
	const refreshBusIntelRef = useRef(refreshBusIntelPreferences)
	refreshBusIntelRef.current = refreshBusIntelPreferences
	const refreshAgentProdRef = useRef(refreshAgentProdPreferences)
	refreshAgentProdRef.current = refreshAgentProdPreferences
	const refreshRecruitRef = useRef(refreshRecruitPreferences)
	refreshRecruitRef.current = refreshRecruitPreferences

	// ── Public refresh(): single method that owns isLoading lifecycle ────
	const refresh = useCallback(async () => {
		if (!selectedCompanyId || !permissions) {
			setCorePreferences(null)
			setBusIntelPreferences(null)
			setAgentProdPreferences(null)
			setRecruitPreferences(null)
			return
		}

		setIsLoading(true)
		try {
			const hasBiAccess = permissions.includes(PERMISSIONS.BROKER_ADMIN_USER)
			const hasRecruitAccess = permissions.includes(PERMISSIONS.RECRUITING_USER)

			const fetches: Promise<void>[] = [
				refreshCoreRef.current(selectedCompanyId),
			]

			if (hasBiAccess) {
				fetches.push(
					refreshBusIntelRef.current(selectedCompanyId),
					refreshAgentProdRef.current(selectedCompanyId)
				)
			}

			if (hasRecruitAccess) {
				fetches.push(refreshRecruitRef.current(selectedCompanyId))
			}

			await Promise.allSettled(fetches)
		} catch (error) {
			console.error('Preferences – refresh() failed:', error)
		} finally {
			setIsLoading(false)
		}
	}, [selectedCompanyId, permissions])

	// ── Effect: trigger refresh when parent contexts are ready ───────────
	// Gated on parent loading states so we don't fire with stale data.
	useEffect(() => {
		if (isUserLoading || isCompanyLoading) {
			return
		}
		refresh()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUserLoading, isCompanyLoading, permissions, sessionData?.accessToken])

	const contextValue = useMemo(
		() => ({
			isLoading,
			corePreferences,
			busIntelPreferences,
			agentProdPreferences,
			recruitPreferences,
			isBusIntelLoading,
			refreshCorePreferences,
			refreshBusIntelPreferences,
			refreshAgentProdPreferences,
			refreshRecruitPreferences,
			updateCorePreferences,
			updateBusIntelPreferences,
			updateAgentProdPreferences,
			updateRecruitPreferences,
		}),
		[
			isLoading,
			corePreferences,
			busIntelPreferences,
			agentProdPreferences,
			recruitPreferences,
			isBusIntelLoading,
			refreshCorePreferences,
			refreshBusIntelPreferences,
			refreshAgentProdPreferences,
			refreshRecruitPreferences,
			updateCorePreferences,
			updateBusIntelPreferences,
			updateAgentProdPreferences,
			updateRecruitPreferences,
		]
	)

	return (
		<PreferencesContext.Provider value={contextValue}>
			{children}
		</PreferencesContext.Provider>
	)
}

export const usePreferences = (): PreferencesContextType => {
	const context = useContext(PreferencesContext)
	if (context === undefined) {
		throw new Error('usePreferences must be used within a PreferencesProvider')
	}
	return context
}
